import { NuplayWordmark } from "./components/NuplayWordmark";
import { useNavigate, Link } from "react-router-dom";

function LogoMark() {
  // NuPlay brand-kit mark in Electric Lime on a dark tile (the brand kit's choice for
  // dark backgrounds), so it stands out on the light header.
  return (
    <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 9, background: "#0A0A0A" }}>
      <img src="/nuplay-symbol-lime.svg" alt="NuPlay" width={22} height={22} />
    </span>
  );
}

const FEATURES = [
  {
    gradient: "from-[#2582EB] to-[#1D6BD8]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.38 2 2 0 0 1 3 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    title: "Instant callback",
    desc: "The AI agent rings you back within seconds — no hold queues, no bots that can't help.",
  },
  {
    gradient: "from-[#34d399] to-[#059669]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    title: "Resolved on the call",
    desc: "Order status, returns, warranty, store support — handled live. No follow-up emails.",
  },
  {
    gradient: "from-[#fbbf24] to-[#f59e0b]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
    title: "Seamless warm handoff",
    desc: "Need a specialist? The AI briefs the human rep with full context before the transfer.",
  },
];

const STATS = [
  { value: "30+",    label: "Enterprises in production" },
  { value: "65%+",   label: "Inbound workflow containment" },
  { value: "60–70%", label: "Faster processing" },
  { value: "24 / 7", label: "Scalable coverage" },
];

// Real NuPlay positioning + customers (nuplay.ai) — for the showcase sections.
const NUPLAY_CAPS = [
  {
    title: "Voice & chat agents",
    desc: "Sales, support, and internal workflows across voice, SMS, email, and chat.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v1a7 7 0 0 1-14 0v-1" /><line x1="12" y1="18" x2="12" y2="22" />
      </svg>
    ),
  },
  {
    title: "Enterprise workflow automation",
    desc: "60–70% faster processing and 65%+ inbound containment, with real-time monitoring.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 4.5v6a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3v3" /><circle cx="6" cy="3.5" r="1.6" /><circle cx="18" cy="20.5" r="1.6" />
      </svg>
    ),
  },
  {
    title: "Seamless human handoff",
    desc: "Full context passed to a live agent — 24/7, across regions and channels.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 2 21 6 17 10" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 22 3 18 7 14" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
  },
];

const CLIENTS = [
  "First Mid Insurance", "Global Schools Group", "NuVision Auto Glass", "Myntra",
  "Aditya Birla Capital", "Discover Market", "Tata Digital", "Super Money",
  "AnyTeam", "cult.fit", "Groww",
];

const SCENARIOS = [
  { label: "Where Is My Order?",      bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.25)",  text: "var(--nx-blue-text)" },
  { label: "Cancel or Modify",        bg: "rgba(244,63,94,0.12)",   border: "rgba(244,63,94,0.25)",   text: "var(--nx-rose-text)" },
  { label: "Store Desk Support",      bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.25)",  text: "var(--nx-green-text)" },
  { label: "Product Recommendation",  bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.25)", text: "var(--nx-violet-text)" },
];

// The three flagship demo flows from the Retail Demo brief — each deep-links to a seeded profile.
const DEMOS = [
  {
    customer: "Ava Thomas", order: "VE-20260545",
    href: "/nurix-experience/experience?agent=order-tracking&persona=elec_CUST-00035&query=wismo",
    title: "Track → cancel → save the sale",
    story: "Her order is still three days out and the timing no longer works, so she asks to cancel.",
    showcases: ["Shipment-state cancellation logic", "Prepaid refund + confirmation emails", "Alternate product with faster delivery"],
  },
  {
    customer: "Elijah Clark", order: "VE-20260536",
    href: "/nurix-experience/experience?agent=order-tracking&persona=elec_CUST-00031&query=store-desk",
    title: "Store pickup → accessory → warm transfer",
    story: "He has a store-pickup order for a phone and wants a compatible charger when he comes in.",
    showcases: ["Pickup-readiness check", "Compatible-accessory recommendation", "Warm transfer to the store team"],
  },
  {
    customer: "Camila White", order: "VE-20260551",
    href: "/nurix-experience/experience?agent=order-tracking&persona=elec_CUST-00038&query=cancellation",
    title: "Price objection → loyalty save",
    story: "She wants to cancel after finding it cheaper elsewhere — but it's really the delivery fee.",
    showcases: ["Pricing-transparency explainer", "Loyalty enrollment", "One-time delivery-fee waiver"],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--nx-bg)", fontFamily: "Nunito, system-ui, sans-serif" }}>

      {/* ── Background atmosphere ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Top-right orb */}
        <div
          className="orb animate-orb_float"
          style={{ width: 700, height: 700, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,130,235,0.13) 0%, transparent 70%)" }}
        />
        {/* Bottom-left orb */}
        <div
          className="orb"
          style={{ width: 500, height: 500, bottom: "5%", left: "-8%", background: "radial-gradient(circle, rgba(37,130,235,0.09) 0%, transparent 70%)", animationDelay: "3s" }}
        />
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.018]"
          style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }}
        />
      </div>

      {/* ── Nav ── */}
      <nav
        className="sticky top-0 z-30 flex-shrink-0"
        style={{ background: "var(--nx-nav)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nx-border)" }}
      >
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark />
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight" style={{ color: "var(--nx-text-1)" }}>NuPlay</span>
              <span className="text-[9px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--nx-text-3)" }}>Electronics</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://nurix-electronics-2.myshopify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-medium transition-all duration-150 hover:scale-105"
              style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)", color: "var(--nx-text-1)" }}
            >
              Visit Store
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7" /><path d="M7 7h10v10" /></svg>
            </a>
            <Link
              to="/nurix-experience/experience"
              className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium transition-all duration-150 hover:scale-105"
              style={{ background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.20)", color: "var(--color-success)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--color-success)" }} />
              AI Agent Online
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div className="relative">
        <div className="relative max-w-6xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20 grid md:grid-cols-[1fr_420px] gap-14 items-start">

          {/* Left — copy */}
          <div className="step-enter">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.09em] uppercase mb-8"
              style={{ background: "rgba(37,130,235,0.12)", border: "1px solid rgba(37,130,235,0.25)", color: "var(--nx-accent-text)" }}
            >
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--nx-accent-text)" }} />
              Customer Support Demo
            </div>

            {/* Headline */}
            <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.08] tracking-[-0.035em] mb-6" style={{ color: "var(--nx-text-1)" }}>
              Support that actually{" "}
              <span style={{
                background: "linear-gradient(135deg, #60BDFA 0%, #2582EB 50%, #1D4885 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                resolves.
              </span>
            </h1>

            <p className="text-[16px] leading-relaxed mb-12 max-w-lg" style={{ color: "var(--nx-text-2)" }}>
              Experience a NuPlay AI-powered voice agent handling real customer support — order tracking, cancellations, store queries, product picks. Fully scripted. Real calls. Seeded data.
            </p>

            {/* Features */}
            <div className="space-y-6">
              {FEATURES.map(({ gradient, icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg`}>
                    {icon}
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold mb-0.5" style={{ color: "var(--nx-text-1)" }}>{title}</div>
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — demo card */}
          <div className="step-enter" style={{ animationDelay: "60ms" }}>
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: "var(--nx-card-bg)",
                border: "1px solid var(--nx-card-border)",
                boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px var(--nx-border)",
                backdropFilter: "blur(20px)",
              }}
            >
              {/* Gradient accent bar */}
              <div className="h-[3px]" style={{ background: "linear-gradient(90deg, #2582EB 0%, #60BDFA 50%, #34d399 100%)" }} />

              <div className="px-7 pt-7 pb-8">
                {/* Agent identity */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #2582EB, #1D6BD8)" }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="22" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[15px] font-bold" style={{ color: "var(--nx-text-1)" }}>Order Tracking Agent</div>
                    <div className="text-[12px]" style={{ color: "var(--nx-text-3)" }}>NuPlay Electronics</div>
                  </div>
                </div>

                <p className="text-[13px] leading-relaxed mb-6" style={{ color: "var(--nx-text-2)" }}>
                  Walk through an end-to-end conversation with seeded customer data and real agent routing. No account needed.
                </p>

                {/* Scenario chips */}
                <div className="mb-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-3" style={{ color: "var(--nx-text-3)" }}>Available scenarios</div>
                  <div className="flex flex-wrap gap-2">
                    {SCENARIOS.map(({ label, bg, border, text }) => (
                      <span
                        key={label}
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                        style={{ background: bg, border: `1px solid ${border}`, color: text }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Multi-agent crew */}
                <div
                  className="flex items-center gap-3 p-3.5 rounded-xl mb-7"
                  style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)" }}
                >
                  <div className="flex items-center flex-shrink-0">
                    {[
                      "linear-gradient(135deg,#f472b6,#ec4899)",
                      "linear-gradient(135deg,#60a5fa,#3b82f6)",
                      "linear-gradient(135deg,#60BDFA,#1D6BD8)"
                    ].map((g, i) => (
                      <span
                        key={i}
                        className="w-6 h-6 rounded-full border-2 flex-shrink-0"
                        style={{ background: g, borderColor: "var(--nx-bg)", marginLeft: i > 0 ? "-6px" : 0 }}
                      />
                    ))}
                  </div>
                  <span className="text-[12px]" style={{ color: "var(--nx-text-2)" }}>
                    Powered by a{" "}
                    <span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>multi-agent crew</span>
                    {" "}— routing, resolution &amp; handoff
                  </span>
                </div>

                {/* CTA */}
                <button
                  onClick={() => navigate("/nurix-experience/experience")}
                  className="w-full h-12 rounded-xl flex items-center justify-center gap-2.5 text-[14px] font-semibold text-white transition-all duration-150 active:scale-[0.985] relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #2582EB 0%, #1D6BD8 100%)", boxShadow: "0 4px 24px rgba(37,130,235,0.4)" }}
                >
                  Start the Demo
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <p className="text-center text-[11px] mt-3.5" style={{ color: "var(--nx-text-3)" }}>
                  No account required · India &amp; US supported
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Flagship demos ── */}
      <div className="max-w-6xl mx-auto px-6 pb-6 md:pb-12">
        <div className="text-center mb-10">
          <div className="text-[11px] font-semibold uppercase tracking-[0.09em] mb-3" style={{ color: "var(--nx-accent-text)" }}>Guided demos</div>
          <h2 className="text-[28px] md:text-[34px] font-extrabold tracking-[-0.03em] mb-3" style={{ color: "var(--nx-text-1)" }}>Three flagship flows to try</h2>
          <p className="text-[15px] max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--nx-text-2)" }}>
            Each starts from a real seeded profile and shows the agent resolving end-to-end — lookup, action, and a warm handoff.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {DEMOS.map((d, i) => (
            <button
              key={d.title}
              onClick={() => navigate(d.href)}
              className="group text-left rounded-2xl p-6 flex flex-col transition-all duration-150 active:scale-[0.99]"
              style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(37,130,235,0.35)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 12px 40px rgba(0,0,0,0.28)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--nx-card-border)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center text-[12px] font-bold flex-shrink-0" style={{ background: "rgba(37,130,235,0.12)", color: "var(--nx-accent-text)" }}>{i + 1}</span>
                <span className="text-[11px] font-medium tabular-nums" style={{ color: "var(--nx-text-3)" }}>{d.customer} · {d.order}</span>
              </div>
              <h3 className="text-[16px] font-bold mb-2 tracking-[-0.01em]" style={{ color: "var(--nx-text-1)" }}>{d.title}</h3>
              <p className="text-[13px] leading-relaxed mb-4" style={{ color: "var(--nx-text-2)" }}>{d.story}</p>
              <div className="text-[10px] font-semibold uppercase tracking-[0.07em] mb-2" style={{ color: "var(--nx-text-3)" }}>What it showcases</div>
              <ul className="space-y-1.5 mb-5">
                {d.showcases.map((s) => (
                  <li key={s} className="flex gap-2 text-[12.5px] leading-snug" style={{ color: "var(--nx-text-2)" }}>
                    <span aria-hidden style={{ color: "var(--nx-accent-text)" }}>•</span><span>{s}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "var(--nx-accent-text)" }}>
                Try this demo
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── What is NuPlay ── */}
      <div style={{ background: "var(--nx-surface2)", borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
            {/* Left — positioning */}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.09em] mb-3" style={{ color: "var(--nx-accent-text)" }}>Powered by NuPlay</div>
              <h2 className="text-[26px] md:text-[32px] font-extrabold leading-[1.14] tracking-[-0.03em] mb-4" style={{ color: "var(--nx-text-1)" }}>
                AI transformation partners for the enterprise
              </h2>
              <p className="text-[15px] leading-relaxed mb-4" style={{ color: "var(--nx-text-2)" }}>
                This support demo runs on <span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>NuPlay</span> — we design and deploy agentic voice &amp; chat systems that execute complex, end-to-end workflows reliably and at scale.
              </p>
              <p className="text-[15px] leading-relaxed mb-7" style={{ color: "var(--nx-text-2)" }}>
                From inbound support to outbound campaigns and internal automation, our agents plug into your existing systems — resolving live on the call, and handing off to a human with full context when it matters.
              </p>
              <a
                href="https://www.nuplay.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold transition-opacity hover:opacity-80"
                style={{ color: "var(--nx-accent-text)" }}
              >
                Learn more about NuPlay
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7" /><path d="M7 7h10v10" /></svg>
              </a>
            </div>

            {/* Right — capability cards */}
            <div className="space-y-3.5">
              {NUPLAY_CAPS.map(({ title, desc, icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 p-4 md:p-5 rounded-2xl"
                  style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}
                >
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(37,130,235,0.12)", color: "var(--nx-accent-text)" }}
                  >
                    {icon}
                  </span>
                  <div>
                    <div className="text-[14.5px] font-bold mb-1" style={{ color: "var(--nx-text-1)" }}>{title}</div>
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Trusted by ── */}
      <div style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <p className="text-center text-[15px] md:text-[16px] leading-relaxed mb-12 max-w-2xl mx-auto" style={{ color: "var(--nx-text-2)" }}>
            <span className="font-bold" style={{ color: "var(--nx-accent-text)" }}>Trusted by 30+ enterprises</span> running AI in production —{" "}
            <span style={{ color: "var(--nx-text-1)" }}>across retail, insurance, financial services, and beyond.</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-10 items-center">
            {CLIENTS.map((name) => (
              <div
                key={name}
                className="text-center text-[15px] md:text-[16px] font-bold tracking-tight transition-colors"
                style={{ color: "var(--nx-text-3)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.color = "var(--nx-text-1)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.color = "var(--nx-text-3)"; }}
              >
                {name}
              </div>
            ))}
            <div className="text-center text-[13px] leading-snug" style={{ color: "var(--nx-text-4)" }}>
              And 30+ more happy clients across the globe
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div style={{ background: "var(--nx-surface2)", borderTop: "1px solid var(--nx-border)", borderBottom: "1px solid var(--nx-border)" }}>
        <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4">
          {STATS.map(({ value, label }, i) => (
            <div
              key={value}
              className="flex flex-col items-center py-2"
              style={{ borderRight: i < STATS.length - 1 ? "1px solid var(--nx-border)" : "none" }}
            >
              <span className="text-[30px] font-bold tracking-tight tabular-nums" style={{ color: "var(--nx-text-1)" }}>{value}</span>
              <span className="text-[12px] mt-1 text-center" style={{ color: "var(--nx-text-3)" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            © 2026 NuPlay Electronics. All rights reserved.
          </span>
          <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            Powered by{" "}
            <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={16} /></span>
          </span>
        </div>
      </footer>
    </div>
  );
}
